'use client';

import { Button } from '@liveauctioneers/hammer-ui-core/button';
import errorLlamaImage from 'public/images/error-llama.png';
import Image from 'next/image';
import proxibidLogo from 'public/images/proxibid.svg';

/**
 * Distinctive error screen inspired by bidder-react's "Melted Mac" pattern.
 * Uses the Hammer UI llama image to create a recognizable full-app crash screen.
 *
 * Design Philosophy:
 * - Highly recognizable: Users and support can instantly identify the error state
 * - Full-screen takeover: Indicates a serious, application-level failure
 * - Professional but friendly: Llama image adds personality while maintaining brand
 *
 * Usage:
 * - Root-level errors (global-error.tsx)
 * - Critical ErrorBoundary fallbacks (when meta.critical === true)
 * - Full application crashes that require restart
 *
 * Styling: Hammer semantic tokens throughout. GlobalErrorContent imports globals.css
 * and Wavebid.css and wraps this component in .hui-wavebid, ensuring all CSS
 * variables and Tailwind utilities resolve correctly in every render path.
 */

type LlamaErrorScreenProps = {
    /** Error object to display (optional) */
    error?: Error;
    /** Callback to navigate home */
    onGoHome?: () => void;
    /** Callback to attempt recovery (resets error boundary) */
    onReset?: () => void;
    /** Show detailed error information (dev mode only) */
    showDetails?: boolean;
};

export const LlamaErrorScreen = ({ error, onGoHome, onReset, showDetails = false }: LlamaErrorScreenProps) => {
    return (
        <div className="font-sans flex min-h-screen w-full flex-col items-center justify-start bg-background-primary px-5 py-10">
            <div className="mb-10 flex w-full max-w-[1200px] justify-center desktop:justify-start">
                <Image
                    alt="Proxibid Seller Portal Logo"
                    height={30}
                    src={proxibidLogo}
                    width={104}
                />
            </div>

            <div className="flex w-full max-w-[1200px] flex-col items-center justify-center gap-10 desktop:flex-row desktop:items-stretch desktop:justify-between desktop:gap-[60px]">
                <div className="flex max-w-[600px] flex-1 flex-col items-center justify-center text-center desktop:items-start desktop:text-left">
                    <h1 className="mb-6 text-4xl leading-tight font-bold text-text-primary desktop:text-5xl">
                        Something went wrong
                    </h1>
                    <p className="mb-8 text-base leading-relaxed text-text-secondary desktop:text-lg">
                        We encountered an unexpected error that prevented the application from working properly. Our
                        team has been notified and we&apos;re working to resolve the issue.
                    </p>
                    <p className="mb-8 text-base leading-relaxed text-text-secondary desktop:text-lg">
                        Please try refreshing the page or returning to the home screen. If the problem persists, contact
                        support for assistance.
                    </p>

                    {showDetails && error && (
                        <details className="mb-8 w-full overflow-x-auto rounded-xs border-l-4 border-border-critical bg-surface-100 p-4 [font-family:'Courier_New',monospace] text-sm text-text-critical">
                            <summary className="mb-2 cursor-pointer font-semibold select-none hover:text-text-critical">
                                Error Details (Development Only)
                            </summary>
                            <div>
                                <strong>Message:</strong> {error.message}
                                {error.stack && (
                                    <>
                                        <br />
                                        <br />
                                        <strong>Stack Trace:</strong>
                                        <pre className="mt-2 whitespace-pre-wrap">{error.stack}</pre>
                                    </>
                                )}
                            </div>
                        </details>
                    )}

                    <div className="flex w-full flex-wrap justify-center gap-3 desktop:w-auto desktop:justify-start">
                        {onReset && (
                            <Button
                                onClick={onReset}
                                size="md"
                                style="primary"
                                variant="filled"
                            >
                                Try Again
                            </Button>
                        )}
                        {onGoHome && (
                            <Button
                                onClick={onGoHome}
                                size="md"
                                variant="outlined"
                            >
                                Go Home
                            </Button>
                        )}
                    </div>
                </div>

                <div className="flex max-w-[320px] shrink-0 flex-col items-center desktop:max-w-[400px]">
                    <div className="mb-4 w-full overflow-hidden rounded-md border border-border-secondary bg-surface-50">
                        <Image
                            alt="Error Llama"
                            height={400}
                            priority
                            src={errorLlamaImage}
                            style={{ display: 'block', height: 'auto', width: '100%' }}
                            width={400}
                        />
                    </div>

                    <div className="max-w-full text-center">
                        <h3 className="mb-2 text-base font-semibold text-text-primary">Error Llama</h3>
                        <p className="text-sm leading-normal text-text-secondary">
                            When things go wrong, this friendly llama reminds us that even errors can be handled with
                            grace.
                            <br />
                            <br />
                            Proxibid Seller Portal by Auction Technology Group
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

LlamaErrorScreen.displayName = 'LlamaErrorScreen';
